import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b_ from 'b_';
import Button from '../Button';
import ConsoleModal from '../ConsoleModal/ConsoleModal';
import { ObservableToggle } from '../Toggle/Toggle';
import Textfield, { ObservableTextfield } from '../Textfield';
import BrowserPoliciesStore from '../../stores/BrowserPolicies/BrowserPoliciesStore';
import AccessCodesEditStore from '../../stores/BrowserPolicies/AccessCodesEditStore';
import Datepicker from '../Datepicker/Datepicker';
import SnackStore from '../../stores/SnackStore';
import Icon from '../Icon/Icon';
import './AccessCodesEditModal.css';

const block = b_.lock('AccessCodesEditModal');

class AccessCodesEditModal extends Component {
    static propTypes = {
        browserPolicies: BrowserPoliciesStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    onSubmit = (event) => {
        event.preventDefault();
        const { browserPolicies } = this.props;
        const { accessCodesEditStore } = browserPolicies;
        const policyName = accessCodesEditStore.policy.name;
        accessCodesEditStore.onAccessCodeSubmit().then((response) => {
            if (accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.ADD) {
                browserPolicies.saveAccessCode(response);
                this.props.snack
                    .showSuccess(`New access code ${response.code} has been added to  ${policyName} policy.`);
            } else {
                browserPolicies.updateAccessCode(response);
                this.props.snack
                    .showSuccess(`Changes to ${policyName} access code ${response.code} has been saved.`);
            }
        }).catch((e) => {
            if (e && accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.ADD) {
                this.props.snack.showError(`Failed to add access code. ${e.message}`);
            }
            if (e && accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT) {
                this.props.snack.showError(`Failed to modify access code. ${e.message}`);
            }
        });
    };

    onCopyToClipboard = () => {
        const { browserPolicies } = this.props;
        const { accessCodesEditStore } = browserPolicies;
        const code = accessCodesEditStore.accessCode;
        const policyName = accessCodesEditStore.policyName;
        if (browserPolicies.copyTextToClipboard(code)) {
            this.props.snack
                .showSuccess(`${policyName} access code ${code} has been copied to your clipboard.`);
        }
    };

    onInputKeydown = (event) => {
        const { accessCodesEditStore } = this.props.browserPolicies;
        const isEditMode = accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT;

        if (event.keyCode === 13 &&
            accessCodesEditStore.isValid() && (!isEditMode || accessCodesEditStore.hasChanged())
        ) {
            this.onSubmit(event);
        }
    };

    onCloseClick = () => {
        const { accessCodesEditStore } = this.props.browserPolicies;
        accessCodesEditStore.closeModal();
    };

    renderAdditionalFields = () => {
        const { accessCodesEditStore } = this.props.browserPolicies;
        return <Fragment>
            <div className={block('FieldTitle', { fixedColor: true })}>Access Code</div>
            <div className={block('FieldContent', { flex: true })}>
                {accessCodesEditStore.accessCode}
                <span className={block('copyToClipboardWrapper')} onClick={this.onCopyToClipboard}>
                    <Icon
                        className={block('copyToClipboardIcon')}
                        type={'assignment'}
                    />
                    {'Copy to clipboard'}
                </span>
            </div>
            <div className={block('FieldTitle', { fixedColor: true })}>Activations</div>
            <div className={block('FieldContent')}>{accessCodesEditStore.activations}</div>
        </Fragment>;
    };

    renderAccessCodeFields = () => {
        const { accessCodesEditStore } = this.props.browserPolicies;
        const { datepickerStore } = accessCodesEditStore;
        const isEditMode = accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT;

        return <Fragment>
            <div className={block('StaticContentWrapper')}>
                <div className={block('FieldTitle', { fixedColor: true })}>Policy Name</div>
                <div className={block('FieldContent')}>{accessCodesEditStore.policyName}</div>
                {isEditMode ? this.renderAdditionalFields() : null}
            </div>
            <div className={block('ContentWrapper')}>
                <div className={block('FieldTitle', { fixedColor: true })}>Never Expire</div>
                <ObservableToggle
                    onLabel={'ON'}
                    offLabel={'OFF'}
                    store={accessCodesEditStore.neverExpire}
                />
                <div className={block('FieldTitle', { fixedColor: true })}>Code Valid Through (GMT)</div>
                {accessCodesEditStore.neverExpire.isTrue ?
                    <Textfield
                        value={'Never Expire'}
                        className={block('DatePickerInputField')}
                        disabled
                    /> : <Datepicker
                        store={datepickerStore}
                        inputClassName={block('DatePickerInputField')}
                    />}
                <div className={block('FieldTitle', { fixedColor: true })}>Unlimited Activations</div>
                <ObservableToggle
                    onLabel={'ON'}
                    offLabel={'OFF'}
                    store={accessCodesEditStore.isUnlimited}
                />
                <div className={block('ActivationsAllowedWrapper')}>
                    {accessCodesEditStore.isUnlimited.isTrue ?
                        <Textfield
                            label="Activation Limit"
                            labelClassName={block('FieldTitle')}
                            value={'Unlimited'}
                            className={block('ActivationsAllowedField')}
                            disabled
                        /> : <ObservableTextfield
                            label="Activation Limit"
                            labelClassName={block('FieldTitle', {
                                fixedColor: accessCodesEditStore.activationsLimit.isValid,
                            })}
                            type="number"
                            className={block('ActivationsAllowedField')}
                            store={accessCodesEditStore.activationsLimit}
                            disabled={accessCodesEditStore.isActivationsFieldDisabled()}
                            onKeyDown={this.onInputKeydown}
                            errorLabel={accessCodesEditStore.activationsLimit.errorMessage}
                            min={0}
                            max={AccessCodesEditStore.MAX_ACTIVATION_LIMIT}
                        />
                    }
                </div>
            </div>
        </Fragment>;
    };

    render() {
        const { accessCodesEditStore } = this.props.browserPolicies;
        const isEditMode = accessCodesEditStore.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT;

        return <ConsoleModal
            isOpen={accessCodesEditStore.isModalOpen.isTrue}
            onClose={this.onCloseClick}
            title={`${isEditMode ? 'Edit' : 'Add'} Access Code`}
            style={{ content: { width: '100%' } }}
            contentClassName={block('ModalContent')}
            headerActions={isEditMode ? <Button
                primaryGreen
                raised
                loading={accessCodesEditStore.updateStatus.isLoading}
                onClick={this.onSubmit}
                disabled={!accessCodesEditStore.hasChanged() || !accessCodesEditStore.isValid()}
            >{'SAVE'}</Button> : <Button
                primaryGreen
                raised
                loading={accessCodesEditStore.createStatus.isLoading}
                onClick={this.onSubmit}
                disabled={!accessCodesEditStore.isValid()}
            >{'ADD'}</Button>}
        >{this.renderAccessCodeFields()}</ConsoleModal>;
    }
}

export default inject('browserPolicies', 'snack')(observer(AccessCodesEditModal));
